/**
 * iter-338t: Editable email confirmation card rendered inside AIChatWidget
 * when the AI detects an "email the report" intent.
 *
 * The parent widget passes the LLM-generated draft; the user can edit
 * To/CC/Subject/Body, optionally BCC themselves, and click Send. On success
 * we render a "Sent" success state with the download link (in case the user
 * wants to copy it out).
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, X as XIcon, Loader2, Check, Copy, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailReportCard({ draft, testIdPrefix = 'ai-chat-email' }) {
  const [to, setTo] = useState((draft.to || []).join(', '));
  const [cc, setCc] = useState((draft.cc || []).join(', '));
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.body || '');
  const [bccMe, setBccMe] = useState(!!draft.bcc_me_default);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // {download_url, expires_at, sent_to}
  const [expanded, setExpanded] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const parseList = (v) => v.split(/[,\s;]+/).map((x) => x.trim()).filter(Boolean);

  const handleSend = async () => {
    const toList = parseList(to);
    if (toList.length === 0) { toast.error('Please add at least one recipient'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/ai-chat/email-report`, {
        report_type: draft.report_type,
        worksheet_id: draft.worksheet_id || null,
        project_id: draft.project_id || null,
        client_id: draft.client_id || null,
        vendor_id: draft.vendor_id || null,
        to: toList,
        cc: parseList(cc),
        subject: subject.trim(),
        body,
        bcc_me: bccMe,
        expiry_hours: draft.expiry_hours || 168,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSent(r.data);
      toast.success('Email sent — link valid for 7 days');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email dispatch failed');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!sent?.download_url) return;
    try {
      await navigator.clipboard.writeText(sent.download_url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — please select the link manually');
    }
  };

  // -----------------------------
  // Sent success state
  // -----------------------------
  if (sent) {
    return (
      <div
        className="rounded-xl p-2.5 text-[11px] border shadow-sm"
        style={{
          background: 'linear-gradient(135deg, rgba(220,252,231,0.85), rgba(209,250,229,0.85))',
          borderColor: 'rgba(16,185,129,0.55)',
        }}
        data-testid={`${testIdPrefix}-sent`}
      >
        <div className="flex items-center gap-1.5 text-emerald-700 font-semibold mb-1">
          <Check size={12} />
          Email sent to {sent.sent_to.length} recipient{sent.sent_to.length !== 1 ? 's' : ''}
        </div>
        <div className="text-stone-600 text-[10px] mb-1 truncate">{(sent.sent_to || []).join(', ')}</div>
        <div className="text-[9.5px] text-stone-500 flex items-center gap-1 mb-1.5">
          <Clock size={9} /> Link valid until {new Date(sent.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
        <button
          type="button"
          onClick={copyLink}
          className="w-full text-[10px] flex items-center justify-center gap-1 py-1 rounded-md bg-white/80 border border-emerald-300 text-emerald-800 hover:bg-white transition-colors"
          data-testid={`${testIdPrefix}-copy-link`}
        >
          <Copy size={10} />
          Copy download link
        </button>
      </div>
    );
  }

  // -----------------------------
  // Draft state — collapsed preview + expand-to-edit
  // -----------------------------
  return (
    <div
      className="rounded-xl border shadow-sm"
      style={{
        background: 'linear-gradient(135deg, rgba(254,243,199,0.75), rgba(253,230,138,0.55))',
        borderColor: 'rgba(180,83,9,0.5)',
      }}
      data-testid={`${testIdPrefix}-draft`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left px-2.5 py-2 flex items-center gap-1.5"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <Mail size={12} className="text-amber-800 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-stone-800 truncate">
            Email {draft.label}
          </div>
          <div className="text-[9.5px] text-stone-500 truncate">
            To: {to || '(add recipient)'} · {draft.target}
          </div>
        </div>
        <span className="text-[9px] uppercase tracking-wider text-amber-800 font-semibold">
          {expanded ? 'Hide' : 'Edit'}
        </span>
      </button>

      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-1.5 border-t border-amber-200/60 pt-2">
          <div>
            <label className="text-[9px] uppercase tracking-wider text-stone-600 font-semibold">To (comma-separated)</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="name@example.com, other@example.com"
              className="w-full text-[11px] px-2 py-1 rounded-md border border-stone-300 bg-white/95 focus:outline-none focus:ring-1 focus:ring-amber-500"
              data-testid={`${testIdPrefix}-to`}
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-stone-600 font-semibold">CC (optional)</label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="cc@example.com"
              className="w-full text-[11px] px-2 py-1 rounded-md border border-stone-300 bg-white/95 focus:outline-none focus:ring-1 focus:ring-amber-500"
              data-testid={`${testIdPrefix}-cc`}
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-stone-600 font-semibold">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full text-[11px] px-2 py-1 rounded-md border border-stone-300 bg-white/95 focus:outline-none focus:ring-1 focus:ring-amber-500"
              data-testid={`${testIdPrefix}-subject`}
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-stone-600 font-semibold">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              className="w-full text-[11px] px-2 py-1 rounded-md border border-stone-300 bg-white/95 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-y"
              data-testid={`${testIdPrefix}-body`}
            />
          </div>
          <label className="flex items-center gap-1.5 text-[10px] text-stone-700 cursor-pointer select-none" data-testid={`${testIdPrefix}-bccme-label`}>
            <input
              type="checkbox"
              checked={bccMe}
              onChange={(e) => setBccMe(e.target.checked)}
              className="w-3 h-3 accent-amber-600"
              data-testid={`${testIdPrefix}-bccme`}
            />
            BCC me for record-keeping
          </label>
          <div className="flex items-center gap-1.5 pt-1">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="flex-1 text-[10px] flex items-center justify-center gap-1 py-1.5 rounded-md bg-amber-700 text-white font-semibold hover:bg-amber-800 disabled:opacity-50 transition-colors"
              data-testid={`${testIdPrefix}-send`}
            >
              {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              {sending ? 'Sending…' : 'Send email'}
            </button>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-[10px] px-2 py-1.5 rounded-md text-stone-600 hover:bg-white/60 transition-colors"
              data-testid={`${testIdPrefix}-cancel`}
            >
              <XIcon size={11} />
            </button>
          </div>
          <p className="text-[9px] text-stone-500 leading-tight">
            The recipient will receive a link to download the {draft.format?.toUpperCase()} — valid for 7 days.
          </p>
        </div>
      )}
    </div>
  );
}
